/**
 * "Add all to bag" for the PDP routine picks (sections/bada-product-routine.liquid, prototype product.js data-add-all).
 *
 * Without JavaScript the wrapped form posts items[][id] to /cart/add and Shopify opens the bag page. With it, the
 * picks go in through one /cart/add.js call and the theme hears about it the same way it does for the product form:
 * a CartLinesUpdateEvent whose promise resolves with the refreshed cart and the re-rendered bag sections, so the bag
 * drawer, the bubble and the free-shipping progress update together.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_STATE_MS = 2000;

class BadaAddAll extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#controller = new AbortController();
    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#onSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#timer) window.clearTimeout(this.#timer);
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-add-all-status]');
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 40);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const button = form.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    const ids = [...form.querySelectorAll('input[name="items[][id]"]')]
      .map((input) => (input instanceof HTMLInputElement ? Number(input.value) : 0))
      .filter(Boolean);
    if (!ids.length) return;

    const routes = /** @type {any} */ (window).Theme?.routes;
    const root = window.Shopify?.routes?.root ?? '/';
    const addUrl = routes?.cart_add_url ?? `${root}cart/add.js`;
    const cartUrl = `${routes?.cart_url ?? `${root}cart`}.js`;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const label = button.textContent ?? '';
    button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })), sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((res) => res.json());

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to bag failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'bada-add-all', itemCount: ids.length },
        });
        this.#announce(data.description || data.message || this.dataset.errorText || '');
        button.removeAttribute('aria-busy');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'bada-add-all',
          itemCount: ids.length,
          sections: data.sections,
        },
      });

      button.textContent = this.dataset.addedText || label;
      button.classList.add('is-added');
      this.#announce(this.dataset.addedMessage || '');
      this.#timer = window.setTimeout(() => {
        button.textContent = label;
        button.classList.remove('is-added');
        button.removeAttribute('aria-busy');
      }, ADDED_STATE_MS);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to bag',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#announce(this.dataset.errorText || '');
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('bada-add-all')) customElements.define('bada-add-all', BadaAddAll);
